import { useToast } from '../../../../shared/components/SharedUI';
import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCatalogo } from "../hooks/useCatalogo";
import { CuentaNode } from "../components/CuentaNode";
import { CuentaModal } from "../components/CuentaModal";
import { downloadExcel, uploadExcel } from "../../shared/excelService";
import { TableToolbar, FilterField, FilterSelect } from "../../shared/TableToolbar";
import styles from "../../invoices/styles/invoice.module.css";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const TIPO_CONFIG = {
  ACTIVO:     { key: "tipoActivo",    icon: "fa-building-columns", color: "#60a5fa" },
  PASIVO:     { key: "tipoPasivo",    icon: "fa-arrow-trend-down", color: "#f87171" },
  PATRIMONIO: { key: "tipoPatrimonio",icon: "fa-shield-halved",    color: "#a78bfa" },
  INGRESO:    { key: "tipoIngreso",   icon: "fa-arrow-trend-up",   color: "#4ade80" },
  GASTO:      { key: "tipoGasto",     icon: "fa-receipt",          color: "#fbbf24" },
};

function contarCuentas(nodo) {
  let total = 1;
  for (const hijo of nodo.subcuentas ?? []) total += contarCuentas(hijo);
  return total;
}

function recolectarClasificaciones(nodos, set = new Set()) {
  for (const n of nodos) {
    if (n.clasificacion) set.add(n.clasificacion);
    if (n.subcuentas?.length) recolectarClasificaciones(n.subcuentas, set);
  }
  return set;
}

export default function ListCatalogo() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const { arbol, loading, error, crear, editar, activar, refetch, previewEliminar, eliminar } = useCatalogo();

  const [expandedIds,       setExpandedIds]       = useState(new Set());
  const [initializedRoots,  setInitializedRoots]  = useState(false);

  if (arbol.length > 0 && !initializedRoots) {
    setExpandedIds(new Set(arbol.map((r) => r.id)));
    setInitializedRoots(true);
  }

  const handleToggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Exportar Excel ─────────────────────────────────────────────────────────
  const [exportando, setExportando] = useState(false);
  async function exportarExcel() {
    setExportando(true);
    try {
      await downloadExcel("/api/finance/catalogo/exportar/", "catalogo_cuentas.xlsx");
    } catch {
      showToast("Error al exportar el catálogo", false);
    } finally {
      setExportando(false);
    }
  }

  const [modal,        setModal]        = useState(null);
  const [confirmDes,   setConfirmDes]   = useState(null);
  const [elimWarning,  setElimWarning]  = useState(null);
  const [elimLoading,  setElimLoading]  = useState(false);
  const [search,       setSearch]       = useState("");
  const [tipoFiltro,   setTipoFiltro]   = useState("");
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // ── Importar catálogo desde Excel ────────────────────────────────────────
  async function handleDescargarPlantilla() {
    try {
      await downloadExcel("/api/finance/catalogo/plantilla/", "plantilla_catalogo.xlsx");
    } catch {
      showToast("Error al descargar la plantilla", false);
    }
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const data = await uploadExcel("/api/finance/catalogo/importar/", file);
      setImportResult(data);
      if ((data.creadas ?? 0) > 0) refetch();
    } catch (err) {
      setImportResult({ creadas: 0, errores: [err.message || "Error de conexión"], omitidas: 0 });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const arbolFiltrado = arbol.filter((r) => !tipoFiltro || r.tipo === tipoFiltro);
  const clasificacionesExistentes = Array.from(recolectarClasificaciones(arbol));

  async function handleCrear(data) { await crear(data); }
  async function handleEditar(data) { await editar(modal.cuenta.id, data); }

  async function handleEliminarClick() {
    setElimLoading(true);
    try {
      const data = await previewEliminar(confirmDes.id);
      setElimWarning(data);
    } catch (e) {
      showToast("Error: " + e.message, false);
      setConfirmDes(null);
    } finally {
      setElimLoading(false);
    }
  }

  async function handleEliminarConfirm() {
    setElimLoading(true);
    try {
      await eliminar(confirmDes.id);
      setConfirmDes(null);
      setElimWarning(null);
    } catch (e) {
      showToast("Error al eliminar: " + e.message, false);
    } finally {
      setElimLoading(false);
    }
  }

  return (
    <>
      {toastEl}

      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={styles.active}> {t("catalogo.titulo")}</span>
      </span>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, margin: "8px 0 20px",
      }}>
        <div>
          <h2 className="title" style={{ margin: 0, color: "var(--text-primary)" }}>
            {t("catalogo.titulo")}
          </h2>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "1rem" }}>
            {t("catalogo.soloDetalle")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* ── Plantilla Excel ── */}
          <button onClick={handleDescargarPlantilla}
            title="Descargar plantilla Excel para importar cuentas"
            style={{
              padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(74,222,128,0.35)",
              background: "rgba(74,222,128,0.08)", color: "#4ade80",
              fontWeight: 600, fontSize: "0.82rem", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = "rgba(74,222,128,0.18)"; }}
            onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(74,222,128,0.08)"; }}
          >
            <i className="fa-solid fa-file-arrow-down" /> Plantilla
          </button>

          {/* ── Importar Excel ── */}
          <button onClick={() => fileInputRef.current?.click()} disabled={importing}
            title="Importar cuentas desde Excel (el código lo genera el sistema)"
            style={{
              padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(96,165,250,0.35)",
              background: "rgba(96,165,250,0.08)", color: "#60a5fa",
              fontWeight: 600, fontSize: "0.82rem", cursor: importing ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
              opacity: importing ? 0.6 : 1,
            }}
            onMouseOver={(e) => { if (!importing) e.currentTarget.style.background = "rgba(96,165,250,0.18)"; }}
            onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; }}
          >
            {importing
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Importando…</>
              : <><i className="fa-solid fa-file-arrow-up" /> Importar Excel</>}
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls"
            style={{ display: "none" }} onChange={handleImportFile} />

          <button onClick={exportarExcel} disabled={loading || exportando || arbol.length === 0}
            style={{
              padding: "8px 14px", background: "var(--hover-bg)",
              border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-secondary)",
              cursor: "pointer", fontSize: "0.82rem",
              display: "flex", alignItems: "center", gap: 6, transition: "color 0.2s",
              opacity: (loading || exportando || arbol.length === 0) ? 0.5 : 1,
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#4ade80")}
            onMouseOut={(e)  => (e.currentTarget.style.color = "var(--text-secondary)")}
          >
            {exportando
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Exportando…</>
              : <><i className="fa-solid fa-file-excel" /> Exportar Excel</>}
          </button>
          <button onClick={refetch}
            style={{
              padding: "8px 14px", background: "var(--hover-bg)",
              border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-secondary)",
              cursor: "pointer", fontSize: "0.82rem",
              display: "flex", alignItems: "center", gap: 6, transition: "color 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseOut={(e)  => (e.currentTarget.style.color = "var(--text-secondary)")}
          >
            <i className="fa-solid fa-arrows-rotate" /> {t("catalogo.actualizar")}
          </button>
        </div>
      </div>

      {/* Tarjetas resumen */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
        gap: 8, marginBottom: 20,
      }}>
        {arbol.map((raiz) => {
          const cfg   = TIPO_CONFIG[raiz.tipo] || TIPO_CONFIG.ACTIVO;
          const label = t(`catalogo.${cfg.key}`);
          const total = contarCuentas(raiz) - 1;
          const activo = tipoFiltro === raiz.tipo;
          return (
            <button key={raiz.id} type="button"
              onClick={() => setTipoFiltro(activo ? "" : raiz.tipo)}
              style={{
                background: activo ? `${cfg.color}18` : "var(--bg-card)",
                border: `1px solid ${activo ? cfg.color + "55" : "var(--border-card)"}`,
                borderRadius: 8, padding: "12px 14px",
                textAlign: "left", cursor: "pointer", transition: "all 0.15s",
              }}
            >
              <i className={`fa-solid ${cfg.icon}`}
                style={{ color: cfg.color, marginBottom: 6, display: "block" }} />
              <div style={{ color: "var(--text-muted)", fontSize: "0.67rem",
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {label}
              </div>
              <div style={{ color: "var(--text-primary)", fontSize: "0.8rem",
                fontWeight: 700, marginTop: 2, fontFamily: "monospace" }}>
                {raiz.codigo}
              </div>
              <div style={{ color: "var(--text-muted)", fontSize: "0.7rem", marginTop: 2 }}>
                {total} {t("catalogo.cuentas")}
              </div>
            </button>
          );
        })}
      </div>

      {/* Tabla árbol */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 8, overflow: "hidden",
        overflowX: "auto",
      }}>
        {/* Barra de filtros unificada */}
        <TableToolbar
          search={search}
          onSearch={setSearch}
          searchPlaceholder={t("catalogo.buscarPlaceholder")}
          loading={loading}
          activeFilterCount={tipoFiltro ? 1 : 0}
          onClearFilters={() => setTipoFiltro("")}
          rightActions={
            <span style={{ fontSize: "0.68rem", color: "var(--text-muted)",
              display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
              <i className="fa-solid fa-circle" style={{ color: "#f97316", fontSize: "0.45rem", marginRight: 4 }} />
              {t("catalogo.detalleNote")}
            </span>
          }
          filters={
            <FilterField label={t("catalogo.tipo")}>
              <FilterSelect value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
                <option value="">{t("common.todos")}</option>
                {Object.entries(TIPO_CONFIG).map(([k, cfg]) => (
                  <option key={k} value={k}>{t(`catalogo.${cfg.key}`)}</option>
                ))}
              </FilterSelect>
            </FilterField>
          }
        />

        {/* Cabecera columnas */}
        <div style={{
          display: "grid", gridTemplateColumns: "38px 8px 112px 1fr auto 80px",
          gap: 0, padding: "6px 14px", minWidth: 480,
          borderBottom: "1px solid var(--border-card)", background: "var(--hover-bg)",
        }}>
          {["", "", t("catalogo.codigo"), t("catalogo.nombre"), t("catalogo.tipo"), t("common.acciones")].map((h, i) => (
            <span key={i} style={{ fontSize: "0.65rem", color: "var(--text-muted)",
              fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {h}
            </span>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
            <i className="fa-solid fa-circle-notch fa-spin"
              style={{ color: "#f97316", fontSize: "1.2rem", marginBottom: 8, display: "block" }} />
            {t("catalogo.cargando")}
          </div>
        )}

        {error && (
          <div style={{ padding: 24, color: "#f87171", textAlign: "center" }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }} />
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {arbolFiltrado
              .filter((r) =>
                !search ||
                r.codigo.includes(search) ||
                r.nombre.toLowerCase().includes(search.toLowerCase())
              )
              .map((raiz) => (
                <CuentaNode
                  key={raiz.id}
                  cuenta={raiz}
                  depth={0}
                  expandedIds={expandedIds}
                  onToggle={handleToggle}
                  onAgregar={(c) => setModal({ type: "create", padre: c })}
                  onEditar={(c)  => setModal({ type: "edit",   cuenta: c })}
                  onDesactivar={(id, nombre) => { setConfirmDes({ id, nombre }); }}
                  onActivar={(id) => activar(id)}
                />
              ))
            }
            {arbolFiltrado.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                {t("catalogo.sinCuentasList")}
              </div>
            )}
          </>
        )}
      </div>

      {/* Resultado de importación */}
      {importResult && (() => {
        const ok = importResult.creadas > 0;
        const accent = ok ? "#4ade80" : "#fbbf24";
        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            {...overlayClose(() => setImportResult(null))}>
            <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
              borderRadius: 14, width: "min(540px,94vw)", maxHeight: "85vh", overflow: "hidden",
              display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.45)" }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 14,
                borderBottom: "1px solid var(--border-card)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                  background: `${accent}1f`, border: `1px solid ${accent}40`,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fa-solid ${ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
                    style={{ color: accent, fontSize: "1.1rem" }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ color: "var(--text-primary)", margin: 0, fontSize: "1.02rem", fontWeight: 700 }}>
                    Resultado de la importación
                  </h3>
                  <p style={{ margin: "3px 0 0", color: "var(--text-muted)", fontSize: "0.79rem" }}>
                    Catálogo de Cuentas
                  </p>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: "18px 24px", overflow: "auto" }}>
                {/* Stats */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: importResult.detalle?.length || importResult.errores?.length ? 16 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px",
                    borderRadius: 20, background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.3)" }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "1rem", color: "#4ade80" }}>
                      {importResult.creadas ?? 0}
                    </span>
                    <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>cuenta(s) creadas</span>
                  </div>
                  {importResult.omitidas > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px",
                      borderRadius: 20, background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)" }}>
                      <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "1rem", color: "#fbbf24" }}>
                        {importResult.omitidas}
                      </span>
                      <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>omitidas</span>
                    </div>
                  )}
                </div>

                {importResult.detalle?.length > 0 && (
                  <div style={{ border: "1px solid var(--border-card)", borderRadius: 10,
                    marginBottom: 14, maxHeight: 220, overflow: "auto" }}>
                    {importResult.detalle.map((c, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 14px", fontSize: "0.82rem",
                        borderBottom: i < importResult.detalle.length - 1 ? "1px solid var(--border-card)" : "none" }}>
                        <i className="fa-solid fa-book-open" style={{ color: "#f97316", fontSize: "0.72rem", flexShrink: 0 }} />
                        <span style={{ fontFamily: "monospace", color: "#f97316", fontWeight: 600, flexShrink: 0 }}>
                          {c.codigo}
                        </span>
                        <span style={{ color: "var(--text-primary)", flex: 1, minWidth: 0 }}>{c.nombre}</span>
                        <span style={{ color: "var(--text-muted)", fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                          bajo {c.padre}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {importResult.errores?.length > 0 && (
                  <div style={{ border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10,
                    background: "rgba(239,68,68,0.06)", maxHeight: 180, overflow: "auto" }}>
                    {importResult.errores.map((err, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8,
                        padding: "9px 14px", fontSize: "0.8rem", color: "#fca5a5",
                        borderBottom: i < importResult.errores.length - 1 ? "1px solid rgba(239,68,68,0.2)" : "none" }}>
                        <i className="fa-solid fa-triangle-exclamation" style={{ marginTop: 2, flexShrink: 0, fontSize: "0.72rem" }} />
                        <span>{err}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "14px 24px",
                borderTop: "1px solid var(--border-card)" }}>
                <button onClick={() => setImportResult(null)}
                  style={{ padding: "9px 22px", background: "#f97316", border: "none",
                    borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: "0.86rem", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 7 }}>
                  <i className="fa-solid fa-check" />Cerrar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal crear/editar */}
      {modal?.type === "create" && (
        <CuentaModal mode="create" padre={modal.padre} onClose={() => setModal(null)} onSave={handleCrear}
          clasificacionesExistentes={clasificacionesExistentes} />
      )}
      {modal?.type === "edit" && (
        <CuentaModal mode="edit" cuenta={modal.cuenta} onClose={() => setModal(null)} onSave={handleEditar}
          clasificacionesExistentes={clasificacionesExistentes} />
      )}

      {/* Eliminar — paso 1: confirmar intención */}
      {confirmDes && !elimWarning && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.65)",
          backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
          {...overlayClose(() => { setConfirmDes(null); setElimWarning(null); })}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 10, padding: 28, width: "min(400px,92vw)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <div style={{ fontSize: "2.2rem", marginBottom: 8 }}>🗑️</div>
              <h3 style={{ color: "var(--text-primary)", margin: 0 }}>¿{t("common.eliminar")} {t("common.nombre").toLowerCase()}?</h3>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginTop: 8 }}>
                {t("common.eliminandoPermanente")}{" "}
                <strong style={{ color: "#f97316" }}>{confirmDes.nombre}</strong>{" "}
                {t("catalogo.eliminarDesc")}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button onClick={() => { setConfirmDes(null); }}
                style={{ padding: "8px 20px", background: "var(--hover-bg)",
                  border: "1px solid var(--border-card)", borderRadius: 7,
                  color: "var(--text-secondary)", cursor: "pointer" }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleEliminarClick} disabled={elimLoading}
                style={{ padding: "8px 20px", background: "rgba(239,68,68,0.15)",
                  border: "1px solid rgba(239,68,68,0.4)", borderRadius: 7, color: "#f87171",
                  cursor: "pointer", fontWeight: 700, opacity: elimLoading ? 0.6 : 1 }}>
                {elimLoading ? t("catalogo.verificando") : t("common.confirmar")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Eliminar — paso 2: advertencia detallada */}
      {confirmDes && elimWarning && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)",
          backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
          {...overlayClose(() => { setConfirmDes(null); setElimWarning(null); })}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 10, padding: 24, width: "min(480px,94vw)", maxHeight: "80vh", overflow: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: "2.2rem", marginBottom: 8 }}>⚠️</div>
              <h3 style={{ color: "var(--text-primary)", margin: 0 }}>{t("catalogo.confirmarEliminar")}</h3>
              <p style={{ color: "#f87171", fontSize: "0.85rem", marginTop: 8, lineHeight: 1.6 }}>
                {elimWarning.advertencia}
              </p>
            </div>
            {elimWarning.cuentas_a_eliminar?.length > 0 && (
              <div style={{ background: "var(--hover-bg)", borderRadius: 6, padding: "10px 14px",
                marginBottom: 16, fontSize: "0.78rem" }}>
                <p style={{ color: "var(--text-muted)", margin: "0 0 6px", fontWeight: 700,
                  textTransform: "uppercase", fontSize: "0.7rem" }}>
                  {t("catalogo.cuentasAEliminar")}
                </p>
                {elimWarning.cuentas_a_eliminar.map((c, i) => (
                  <div key={i} style={{ color: "var(--text-primary)", fontFamily: "monospace", marginBottom: 2 }}>
                    {c.codigo} — {c.nombre}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button onClick={() => { setConfirmDes(null); setElimWarning(null); }}
                style={{ padding: "9px 20px", background: "var(--hover-bg)",
                  border: "1px solid var(--border-card)", borderRadius: 7,
                  color: "var(--text-secondary)", cursor: "pointer" }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleEliminarConfirm} disabled={elimLoading}
                style={{ padding: "9px 20px", background: "rgba(239,68,68,0.15)",
                  border: "1px solid rgba(239,68,68,0.4)", borderRadius: 7, color: "#f87171",
                  cursor: "pointer", fontWeight: 700, opacity: elimLoading ? 0.6 : 1 }}>
                {elimLoading ? t("common.eliminando") : t("catalogo.siEliminarPermanente")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
